import type {
  OffreData,
  HeroCta,
  IntroMessages,
  StrategicStakes,
  PricingGrid,
  CaseStudy,
  FaqSection,
} from "@/db/schema/content-items";
import {
  DEFAULT_HERO_CTA_PROMPT,
  DEFAULT_MESSAGES_PROMPT,
  DEFAULT_STAKES_PROMPT,
  DEFAULT_PRICING_PROMPT,
  DEFAULT_CASE_STUDY_PROMPT,
  DEFAULT_FAQ_PROMPT,
} from "@/lib/content/ai-prompts";
import { stakesToText } from "@/lib/content/stakes";
import { buildSectionsDisponibles } from "@/lib/content/sections-disponibles";
import { getRealisation } from "@/lib/content/realisations-bank";

// Registre des sections de `/[slug]` regénérables directement depuis la page
// publique en mode admin (bouton flottant + panneau latéral). Une seule source
// de vérité par section : le prompt par défaut, comment construire le body de
// la requête à partir de l'état courant, et comment appliquer le résultat sur
// `OffreData`. Réutilise les mêmes routes `POST /api/super-admin/content/generate-*`
// que l'admin classique (offre-form.tsx) et src/lib/content/generators.ts —
// aucune logique de génération dupliquée ici, seulement l'orchestration côté page.

export type AdminSectionKey = "hero" | "intro" | "enjeux" | "tarifs" | "reference" | "faq";

export interface AdminSectionContext {
  data: OffreData;
  publicUrl: string;
}

export interface AdminSectionDescriptor {
  key: AdminSectionKey;
  label: string;
  defaultPrompt: string;
  // Dernier prompt effectivement envoyé (référence, pré-remplit l'éditeur) ;
  // null si la section n'a jamais été générée.
  currentPrompt: (data: OffreData) => string | null;
  endpoint: string;
  buildBody: (prompt: string, ctx: AdminSectionContext) => Record<string, unknown>;
  applyResult: (json: unknown) => Partial<OffreData>;
  summarize: (data: OffreData) => string;
  note?: string;
}

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n)}…` : s);

export const ADMIN_SECTIONS: AdminSectionDescriptor[] = [
  {
    key: "hero",
    label: "Hero (ouverture de page)",
    defaultPrompt: DEFAULT_HERO_CTA_PROMPT,
    currentPrompt: (d) => d.heroCta?.generatedPrompt ?? null,
    endpoint: "/api/super-admin/content/generate-hero-cta",
    buildBody: (prompt, ctx) => ({
      language: ctx.data.language ?? "fr",
      brief: ctx.data.initialRequest,
      prompt,
      messagingHouse: ctx.data.messagingHouse?.trim() || "",
    }),
    applyResult: (json) => ({ heroCta: (json as { heroCta: HeroCta }).heroCta }),
    summarize: (d) =>
      d.heroCta
        ? `${d.heroCta.title}${d.heroCta.subtitle ? " : " + d.heroCta.subtitle : ""}`
        : "Pas encore généré",
  },
  {
    key: "intro",
    label: "Mot de page & teaser",
    defaultPrompt: DEFAULT_MESSAGES_PROMPT,
    currentPrompt: (d) => d.introMessages?.generatedPrompt ?? null,
    endpoint: "/api/super-admin/content/generate-messages",
    buildBody: (prompt, ctx) => ({
      language: ctx.data.language ?? "fr",
      brief: ctx.data.initialRequest,
      prompt,
      urlProposition: ctx.publicUrl,
      messagingHouse: ctx.data.messagingHouse?.trim() || "",
      sectionsDisponibles: buildSectionsDisponibles(ctx.data),
    }),
    applyResult: (json) => ({ introMessages: (json as { introMessages: IntroMessages }).introMessages }),
    summarize: (d) => (d.introMessages?.motDePage ? truncate(d.introMessages.motDePage, 160) : "Pas encore généré"),
  },
  {
    key: "enjeux",
    label: "Enjeux stratégiques",
    defaultPrompt: DEFAULT_STAKES_PROMPT,
    currentPrompt: (d) => d.strategicStakes?.generatedPrompt ?? null,
    endpoint: "/api/super-admin/content/generate-stakes",
    buildBody: (prompt, ctx) => ({ brief: ctx.data.initialRequest, prompt, language: ctx.data.language ?? "fr" }),
    applyResult: (json) => ({
      strategicStakes: (json as { strategicStakes: StrategicStakes }).strategicStakes,
    }),
    summarize: (d) =>
      d.strategicStakes?.items?.length
        ? d.strategicStakes.items.map((i) => i.enjeu).join(", ")
        : "Pas encore générés",
    note: "Ne régénère pas les maquettes des enjeux (bouton séparé, à côté) : elles peuvent devenir décalées si le texte change beaucoup.",
  },
  {
    key: "tarifs",
    label: "Grille tarifaire",
    defaultPrompt: DEFAULT_PRICING_PROMPT,
    currentPrompt: (d) => d.pricingGrid?.generatedPrompt ?? null,
    endpoint: "/api/super-admin/content/generate-pricing",
    buildBody: (prompt, ctx) => ({
      language: ctx.data.language ?? "fr",
      brief: ctx.data.initialRequest,
      prompt,
      enjeux: stakesToText(ctx.data.strategicStakes),
    }),
    applyResult: (json) => ({ pricingGrid: (json as { pricingGrid: PricingGrid }).pricingGrid }),
    summarize: (d) =>
      d.pricingGrid?.lines?.length ? `${d.pricingGrid.lines.length} ligne(s)` : "Pas encore générée",
  },
  {
    key: "reference",
    label: "Étude de cas",
    defaultPrompt: DEFAULT_CASE_STUDY_PROMPT,
    currentPrompt: (d) => d.caseStudy?.generatedPrompt ?? null,
    endpoint: "/api/super-admin/content/generate-case-study",
    buildBody: (prompt, ctx) => ({
      language: ctx.data.language ?? "fr",
      brief: ctx.data.initialRequest,
      prompt,
      enjeux: stakesToText(ctx.data.strategicStakes),
    }),
    applyResult: (json) => ({ caseStudy: (json as { caseStudy: CaseStudy }).caseStudy }),
    summarize: (d) =>
      d.caseStudy?.realisationId
        ? (getRealisation(d.caseStudy.realisationId)?.title ?? d.caseStudy.realisationId)
        : "Pas encore choisie",
  },
  {
    key: "faq",
    label: "FAQ",
    defaultPrompt: DEFAULT_FAQ_PROMPT,
    currentPrompt: (d) => d.faq?.generatedPrompt ?? null,
    endpoint: "/api/super-admin/content/generate-faq",
    buildBody: (prompt, ctx) => ({
      language: ctx.data.language ?? "fr",
      brief: ctx.data.initialRequest,
      prompt,
      budgetRange: ctx.data.prospectBudgetRange ?? null,
      messagingHouse: ctx.data.messagingHouse?.trim() || "",
    }),
    applyResult: (json) => ({ faq: (json as { faq: FaqSection }).faq }),
    summarize: (d) => (d.faq?.items?.length ? `${d.faq.items.length} question(s)` : "Pas encore générée"),
  },
];

export function getAdminSection(key: AdminSectionKey): AdminSectionDescriptor {
  const found = ADMIN_SECTIONS.find((s) => s.key === key);
  if (!found) throw new Error(`Section admin inconnue : ${key}`);
  return found;
}

// Sections fixes (contenu en dur, pas d'IA) : affichées dans la sidebar pour
// la vue d'ensemble, sans bouton de régénération.
export interface FixedSectionInfo {
  id: string;
  label: string;
  file: string;
}
export const FIXED_SECTIONS: FixedSectionInfo[] = [
  { id: "methode", label: "Méthode (comment ça se passe)", file: "how-it-works.tsx" },
  { id: "confiance", label: "Confiance", file: "trust-proofs.tsx" },
  { id: "reserver", label: "Réservation", file: "booking-section.tsx" },
];
